import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Avatar } from '../../../../../../../components';
import { dottedEnvelope, envelope } from './assets';

type Sender = {
  email: string;
  metadata: {
    name: string;
    avatarSrc: string | null;
  };
};

type Recipient = {
  type: string;
  metadata: {
    name: string;
    avatarSrc: string | null;
  };
};

type Props = {
  from?: Sender;
  recipients?: Recipient[];
  sentAt?: string;
  isDottedEnvelope: boolean;
};

export const EmailHeader: React.FC<Props> = ({
  from,
  recipients,
  sentAt,
  isDottedEnvelope,
}): JSX.Element => {
  const [tooltipFromOpen, setTooltipFromOpen] = useState(false);

  const [toTooltipOpen, setToTooltipOpen] = useState(false);

  const [ccTooltipOpen, setCcTooltipOpen] = useState(false);

  const toggleTooltipFrom = () => {
    setTooltipFromOpen(!tooltipFromOpen);
  };

  const toggleToTooltip = () => {
    setToTooltipOpen(!toTooltipOpen);
  };

  const toggleCcTooltip = () => {
    setCcTooltipOpen(!ccTooltipOpen);
  };

  const valueFromRef = useRef<HTMLDivElement>(null);

  const valueToRef = useRef<HTMLDivElement>(null);

  const valueCcRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;
    const isOnTarget =
      valueToRef.current?.contains(target) ||
      valueCcRef.current?.contains(target) ||
      valueFromRef.current?.contains(target);
    if (!isOnTarget && (toTooltipOpen || valueCcRef || valueFromRef)) {
      setToTooltipOpen(false);
      setCcTooltipOpen(false);
      setTooltipFromOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () =>
      document.removeEventListener('click', handleClickOutside, true);
  });

  const recipientsTo = recipients
    ?.filter((el) => el.type === 'to')
    ?.map((el) => ({ name: el.metadata.name, avatar: el.metadata.avatarSrc }));

  const recipientsCc = recipients
    ?.filter((el) => el.type === 'cc')
    ?.map((el) => ({ name: el.metadata.name, avatar: el.metadata.avatarSrc }));

  const attrs = {
    wrapper: { className: 'email-header' },

    imgSection: {
      wrapper: {
        className: 'email-header__img-section',
      },

      svg: {
        className: 'email-header__img-section-svg',
        src: isDottedEnvelope ? dottedEnvelope : envelope,
      },
    },

    leftSection: { className: 'email-header__left-section' },

    from: {
      wrapper: { className: 'email-header__left-section__from' },

      title: { className: 'email-header__left-section__from-title' },

      value: {
        className: 'email-header__left-section__from-value',
        onMouseEnter: toggleTooltipFrom,
        onMouseLeave: toggleTooltipFrom,
        ref: valueFromRef,
      },

      tooltip: {
        wrapper: {
          className: tooltipFromOpen
            ? 'open email-header__left-section__from-value__tooltip'
            : 'email-header__left-section__from-value__tooltip',
        },

        participant: {
          className:
            'email-header__left-section__from-value__tooltip__participant',
        },

        avatar: {
          className:
            'email-header__left-section__from-value__tooltip__participant-avatar',
          isTwentyPx: true,
        },

        name: {
          className:
            'email-header__left-section__from-value__tooltip__participant-name',
        },
      },
    },

    centralSection: {
      wrapper: { className: 'email-header__left-section__central' },

      titleTo: { className: 'email-header__left-section__central-title-to' },

      valueTo: {
        className: 'email-header__left-section__central-title-to-value',
        onMouseEnter: toggleToTooltip,
        onMouseLeave: toggleToTooltip,
        ref: valueToRef,
      },

      valueToText: {
        className: 'email-header__left-section__central-title-to-value-text',
      },

      valueCircleTo: {
        className: 'email-header__left-section__central-title-to-value-circle',
      },

      tooltipTo: {
        wrapper: {
          className: toTooltipOpen
            ? 'open email-header__left-section__central-title-to-value__tooltip'
            : 'email-header__left-section__central-title-to-value__tooltip',
        },

        participant: {
          className:
            'email-header__left-section__central-title-to-value__tooltip__participant',
        },

        avatar: {
          className:
            'email-header__left-section__central-title-to-value__tooltip__participant-avatar',
          isTwentyPx: true,
        },

        name: {
          className:
            'email-header__left-section__central-title-to-value__tooltip__participant-name',
        },
      },

      titleCc: { className: 'email-header__left-section__central-title-cc' },

      valueCc: {
        className: 'email-header__left-section__central-title-cc-value',
        onMouseEnter: toggleCcTooltip,
        onMouseLeave: toggleCcTooltip,
        ref: valueCcRef,
      },

      valueCcText: {
        className: 'email-header__left-section__central-title-cc-value-text',
      },

      valueCircleCc: {
        className: 'email-header__left-section__central-title-cc-value-circle',
      },

      tooltipCc: {
        wrapper: {
          className: ccTooltipOpen
            ? 'open email-header__left-section__central-title-to-value__tooltip'
            : 'email-header__left-section__central-title-to-value__tooltip',
        },

        participant: {
          className:
            'email-header__left-section__central-title-to-value__tooltip__participant',
        },

        avatar: {
          className:
            'email-header__left-section__central-title-to-value__tooltip__participant-avatar',
          isTwentyPx: true,
        },

        name: {
          className:
            'email-header__left-section__central-title-to-value__tooltip__participant-name',
        },
      },
    },

    date: { className: 'email-header-date' },
  };

  const createTooltipFrom = () => {
    let tooltipFrom: JSX.Element | null = null;

    if (from?.metadata?.name) {
      tooltipFrom = (
        <div {...attrs.from.tooltip.wrapper}>
          <div {...attrs.from.tooltip.participant}>
            <Avatar
              image={from?.metadata?.avatarSrc || ''}
              userName={from?.metadata?.name}
              {...attrs.from.tooltip.avatar}
            />
            <span {...attrs.from.tooltip.name}>{from?.metadata?.name}</span>
          </div>
        </div>
      );
    }

    return { tooltipFrom };
  };

  const defineTooltipTo = () => {
    let tooltipTo: JSX.Element | null = null;

    if (recipientsTo?.length) {
      const tooltipData = (
        <div {...attrs.centralSection.tooltipTo.wrapper}>
          {recipientsTo.map((el, i) => (
            <div
              {...attrs.centralSection.tooltipTo.participant}
              key={`${el.name}${i}`}
            >
              <Avatar
                image={el?.avatar ? el?.avatar : ''}
                userName={el?.name}
                {...attrs.centralSection.tooltipTo.avatar}
              />
              <span {...attrs.centralSection.tooltipTo.name}>{el.name}</span>
            </div>
          ))}
        </div>
      );

      tooltipTo = tooltipData;
    }

    return { tooltipTo };
  };

  const defineTooltipCc = () => {
    let tooltipCc: JSX.Element | null = null;

    if (recipientsCc?.length) {
      const tooltipData = (
        <div {...attrs.centralSection.tooltipCc.wrapper}>
          {recipientsCc.map((el, i) => (
            <div
              {...attrs.centralSection.tooltipCc.participant}
              key={`${el.name}${i}`}
            >
              <Avatar
                image={el?.avatar ? el?.avatar : ''}
                userName={el?.name}
                {...attrs.centralSection.tooltipCc.avatar}
              />
              <span {...attrs.centralSection.tooltipCc.name}>{el.name}</span>
            </div>
          ))}
        </div>
      );

      tooltipCc = tooltipData;
    }

    return { tooltipCc };
  };

  const { tooltipFrom } = createTooltipFrom();

  const { tooltipCc } = defineTooltipCc();

  const { tooltipTo } = defineTooltipTo();

  const mainRecipient = recipientsTo?.length ? recipientsTo[0].name : '';

  const circleTo =
    typeof recipientsTo?.length === 'number' && recipientsTo.length - 1 > 0 ? (
      <div {...attrs.centralSection.valueCircleTo}>
        +{recipientsTo.length - 1}
      </div>
    ) : null;

  const firstRecipientCc = recipientsCc?.length ? recipientsCc[0].name : '';

  const circleCc =
    typeof recipientsCc?.length === 'number' && recipientsCc.length - 1 > 0 ? (
      <div {...attrs.centralSection.valueCircleTo}>
        +{recipientsCc.length - 1}
      </div>
    ) : null;

  const date = sentAt
    ? moment(sentAt).format('MMM D, YYYY, h:mm A')
    : moment().format('MMM D, YYYY, h:mm A');

  return (
    <div {...attrs.wrapper}>
      <div {...attrs.imgSection.wrapper}>
        <img {...attrs.imgSection.svg} alt="" />
      </div>

      <div {...attrs.leftSection}>
        <div {...attrs.from.wrapper}>
          <span {...attrs.from.title}>From:</span>
          <span {...attrs.from.value}>
            {from?.metadata?.name} {tooltipFrom}
          </span>
        </div>
        <div {...attrs.centralSection.wrapper}>
          <span {...attrs.centralSection.titleTo}>To:</span>
          <span {...attrs.centralSection.valueTo}>
            <span {...attrs.centralSection.valueToText}>{mainRecipient}</span>{' '}
            {circleTo} {tooltipTo}
          </span>
          <span {...attrs.centralSection.titleCc}>CC:</span>
          <span {...attrs.centralSection.valueCc}>
            <span {...attrs.centralSection.valueCcText}>
              {firstRecipientCc}
            </span>{' '}
            {circleCc} {tooltipCc}
          </span>
        </div>
      </div>

      <div {...attrs.date}>{date}</div>
    </div>
  );
};
